import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Box, Flex, Text, Center, useToast, Button, Avatar, Tooltip, FormControl, Switch } from '@chakra-ui/react';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import { faPrint, faEdit, faHistory } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import c from '../../constant';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import useStore from '../../store';
import PrintLayout from '../../components/Print/PrintLayout';
import LabelDetail from '../../components/LabelDetail';
import DownloadAndViewButton from '../../components/Files/DownloadAndViewButton';
import UserDislayNameById from '../../components/UserDislayNameById';
import getPositionLabel from '../../utils/getPositionLabel';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const QUERY_DETAIL = gql`
	query get($id: String!) {
		getTrainingRecordById(id: $id) {
			id
			no
			courseTitle
			organiser
			certificate
			dateStart
			dateEnd
			location
			locationType
			means
			type
			attachmentFile
			bioListing
			updatedBy
			updatedAt
			updatedByUser {
				id
				username
				fullName
				fullNameKhmer
				department
				position
				avatar
			}
			user {
				id
				username
				fullName
				fullNameKhmer
				department
				position
				avatar
			}
			notification {
				id
				read
			}
		}
	}
`;

const MUTATION_SWITCH_BIO_LISTING = gql`
	mutation updateTrainingRecordBioListing($id: String!, $bioListing: Boolean!) {
		updateTrainingRecordBioListing(id: $id, bioListing: $bioListing)
	}
`;

const MUTATION_READ_NOTIFICATION = gql`
	mutation read($id: String!) {
		readNotificationById(id: $id)
	}
`;

const LABEL_WIDTH = '180px';

function TrainingRecordDetail() {
	const { currentLang } = useStore((state) => state.currentLang);
	const { currentUser: user } = useStore((state) => state.currentUser);
	const { countUnreadNotifications } = useStore((state) => state.countUnreadNotifications);
	const setCountUnreadNotifications = useStore((state) => state.setCountUnreadNotifications);
	moment.locale(currentLang === 'kh' ? 'km' : 'en-gb');
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const id = urlParams.get('id') ? urlParams.get('id') : null;
	const toast = useToast();
	const [record, setRecord] = useState(null);
	const [canListToBio, setCanListToBio] = useState(false);
	const [loadDetail, { data }] = useLazyQuery(QUERY_DETAIL, { fetchPolicy: 'no-cache' });
	const [readNotification, {}] = useMutation(MUTATION_READ_NOTIFICATION);
	const [updateBioListing, { loading: loadingUpdateBioListing }] = useMutation(MUTATION_SWITCH_BIO_LISTING);

	useEffect(getDetail, [id]);

	useEffect(() => {
		const detail = data?.getTrainingRecordById;

		setRecord(detail);
		setCanListToBio(detail?.bioListing === true);
	}, [data]);

	function getDetail() {
		id && loadDetail({ variables: { id } });
	}

	useEffect(() => {
		if (record?.notification?.read === false) {
			setCountUnreadNotifications(countUnreadNotifications - 1);
			readNotification({ variables: { id: record?.notification?.id } });
		}
	}, [record]);

	const canSubmitCourse = user?.canSubmitTrainingCourse === true;

	return (
		<>
			<PrintLayout>
				<>
					<Container maxW="container.xl" mt="16px" mb="16px">
						<BreadcrumbComponent
							list={[
								{
									name: 'Training Course',
									path: `/training-course`,
								},
								{
									name: 'Detail',
									path: '#',
								},
							]}
						/>
						<Center mt="5">
							<Box
								w="100%"
								bg="white"
								borderRadius={c.borderRadius}
								border="1px solid #dbdbdb"
								boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
								pb="32px"
							>
								<Flex
									alignItems="center"
									justifyContent="space-between"
									bg="#FAFAFA"
									p="16px"
									pt="8px"
									pb="8px"
									mb="16px"
									borderBottom="1px solid #dbdbdb"
								>
									<Flex justifyContent="center" alignItems="center">
										<Text fontSize="x-large" fontWeight="bold" mr="4">
											{t('Detail')}
										</Text>
									</Flex>

									<Flex className="hidden-print">
										<Button
											onClick={() => history.push(`/training-course/history?id=${id}`)}
											leftIcon={<FontAwesomeIcon icon={faHistory} style={{ fontSize: 16 }} />}
											colorScheme="gray"
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											ml={2}
										>
											{t('History')}
										</Button>

										{canSubmitCourse && (
											<Button
												onClick={() => history.push(`/training-course/update?id=${id}`)}
												leftIcon={<FontAwesomeIcon icon={faEdit} style={{ fontSize: 16 }} />}
												colorScheme="gray"
												variant="solid"
												size="sm"
												borderRadius={c.borderRadius}
												border="1px solid #bdc3c7"
												ml={2}
											>
												{t('Update')}
											</Button>
										)}
										<Button
											onClick={() => window?.print()}
											leftIcon={<FontAwesomeIcon icon={faPrint} style={{ fontSize: 16 }} />}
											colorScheme="gray"
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											ml={2}
										>
											{t('Print')}
										</Button>
									</Flex>
								</Flex>

								<Box p="10px" ml={2}>
									<LabelDetail
										mb={3}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Officials"
										childDetail={
											<Flex direction="column">
												<Tooltip
													label={
														<Box textAlign="center">
															<UserDislayNameById id={record?.user?.id} />
															{record?.user?.position?.length > 0 ? ', ' : ''}
															{record?.user?.position
																? getPositionLabel(record?.user?.position, currentLang === 'kh' ? 'kh' : 'en')
																: ''}
														</Box>
													}
												>
													<Box textAlign="center">
														<Avatar
															name={currentLang === 'kh' ? record?.user?.fullNameKhmer : record?.user?.fullName}
															src={record?.user?.avatar?.icon ? c.baseFilePath + record?.user?.avatar?.icon : undefined}
														/>
														<Text textAlign="center">
															{currentLang === 'kh' ? record?.user?.fullNameKhmer : record?.user?.fullName || 'N/A'}
														</Text>
													</Box>
												</Tooltip>
											</Flex>
										}
									/>

									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Number"
										titleDetail={record?.no ? String(record?.no).padStart(4, '0') : 'N/A'}
									/>

									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Course Title"
										titleDetail={record?.courseTitle}
									/>

									<LabelDetail mb={1} mt={2} labelWidth={LABEL_WIDTH} direction="row" label="Organiser" titleDetail={record?.organiser} />

									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Certificate"
										titleDetail={record?.certificate || 'N/A'}
									/>

									<LabelDetail mb={1} mt={2} labelWidth={LABEL_WIDTH} direction="row" label="location" titleDetail={record?.location} />

									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Date From"
										titleDetail={record?.dateStart ? moment(record?.dateStart).format('LLLL') : 'N/A'}
									/>

									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Date To"
										titleDetail={record?.dateEnd ? moment(record?.dateEnd).format('LLLL') : 'N/A'}
									/>

									<LabelDetail mb={1} mt={2} labelWidth={LABEL_WIDTH} direction="row" label="Means" titleDetail={record?.means} />

									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Location Type"
										titleDetail={record?.locationType}
									/>

									<LabelDetail mb={1} mt={2} labelWidth={LABEL_WIDTH} direction="row" label="Type" titleDetail={record?.type} />

									<Flex alignItems="center" mt={2} mb={1}>
										<Text fontWeight="semibold" minW="195px" w="195px">
											{t('Attachment File')}
										</Text>

										{record?.attachmentFile ? (
											<Flex className="viewContainer" alignItems="center" mt="2">
												<DownloadAndViewButton
													minW="150px"
													url={record?.attachmentFile?.file}
													size={record?.attachmentFile?.size}
													marginLeft={0}
												/>
											</Flex>
										) : (
											'N/A'
										)}
									</Flex>

									{/* {record?.updatedBy !== null && (
										<>
											<LabelDetail
												mb={1}
												mt={2}
												labelWidth={LABEL_WIDTH}
												direction="row"
												label="Updated By"
												childDetail={
													<Flex direction="column">
														<Tooltip
															label={
																<Box textAlign="center">
																	<UserDislayNameById id={record?.updatedByUser?.id} />
																	{record?.updatedByUser?.position?.length > 0 ? ', ' : ''}
																	{record?.updatedByUser?.position
																		? getPositionLabel(record?.updatedByUser?.position, currentLang === 'kh' ? 'kh' : 'en')
																		: ''}
																</Box>
															}
														>
															<Box textAlign="center">
																<Avatar
																	name={
																		currentLang === 'kh'
																			? record?.updatedByUser?.fullNameKhmer
																			: record?.updatedByUser?.fullName
																	}
																	src={
																		record?.updatedByUser?.avatar?.icon
																			? c.baseFilePath + record?.updatedByUser?.avatar?.icon
																			: undefined
																	}
																/>
																<Text textAlign="center">
																	{currentLang === 'kh'
																		? record?.updatedByUser?.fullNameKhmer
																		: record?.updatedByUser?.fullName || 'N/A'}
																</Text>
															</Box>
														</Tooltip>
													</Flex>
												}
											/>

											<LabelDetail
												mb={1}
												mt={2}
												labelWidth={LABEL_WIDTH}
												direction="row"
												label="Updated At"
												titleDetail={moment(record?.updatedAt).format('LLLL')}
											/>
										</>
									)} */}

									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={currentLang === 'kh' ? '160px' : '160px'}
										direction="row"
										label="List Course to Bio"
										childDetail={
											<FormControl display="flex" alignItems="center" mt={1} ml="20px">
												<Switch
													isChecked={canListToBio}
													id="bioListing"
													colorScheme="blue"
													size="md"
													isDisabled={loadingUpdateBioListing}
													onChange={async (e) => {
														const checked = e.target.checked;

														setCanListToBio(checked);

														try {
															await updateBioListing({
																variables: {
																	id,
																	bioListing: checked,
																},
															});

															toast({
																title: checked
																	? t('Training course has been listed to bio')
																	: t('Training course has been removed from bio'),
																status: checked ? 'success' : 'warning',
																isClosable: true,
																position: 'top-right',
																duration: 3000,
															});

															getDetail();
														} catch (error) {
															setCanListToBio(!checked);

															toast({
																title: t(error?.message || 'something went wrong'),
																status: 'error',
																isClosable: true,
																position: 'bottom-right',
																duration: 5000,
															});
														}
													}}
												/>
											</FormControl>
										}
									/>
								</Box>
							</Box>
						</Center>
					</Container>
				</>
			</PrintLayout>
		</>
	);
}

export default TrainingRecordDetail;
